import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '../ui/Icon';
import { Bouton, CaseACocher, Liste, Saisie, Segmente, ZoneTexte, cn } from '../ui/kit';
import { BlocPaiement, Fil, clsBouton } from './partages';
import { useBoutique } from '../state/boutique';
import { arrhes, eur, fraisLivraison, kg, prochainsCreneaux } from '../data/repository';
import { BOUTIQUES, REGLAGES } from '../data/mock-data';

/**
 * TUNNEL DE COMMANDE — trois étapes, une question par écran.
 *
 * Principes de formulaire (la revue les réclamait explicitement) :
 *   · libellé **au-dessus** du champ, jamais un placeholder qui sert de libellé ;
 *   · 52 px de haut, une seule bordure, un focus net, une erreur qui dit quoi faire ;
 *   · aucune validation silencieuse : on explique, on ne bloque pas sans raison ;
 *   · le récapitulatif reste visible — le client ne perd jamais le montant de vue.
 */
const ETAPES = ['Service', 'Coordonnées', 'Paiement'] as const;

interface Recap {
  numero: string;
  prenom: string;
  mode: 'PICKUP' | 'DELIVERY';
  jour: string;
  heures: string;
  dateIso: string;
  boutiqueNom: string;
  boutiqueAdresse: string;
  total: number;
  arrhes: number;
  solde: number;
  lignes: Array<{ nom: string; quantite: number; total: number }>;
}

interface Form {
  prenom: string; nom: string; email: string; telephone: string; note: string;
  rue: string; codePostal: string; ville: string;
  creneau: string;
  paiement: 'ARRIES' | 'COMPTOIR' | 'EN_LIGNE';
  carte: string; expiration: string; cvc: string; cgv: boolean;
}

export function Commande() {
  const { lignes, nbArticles, sousTotal, mode, setMode, boutiqueId, setBoutique, vider } = useBoutique();
  const [etape, setEtape] = useState(1);
  const [erreurs, setErreurs] = useState<Record<string, string>>({});
  const [confirmee, setConfirmee] = useState<string | null>(null);
  const [recap, setRecap] = useState<Recap | null>(null);
  const creneaux = useMemo(() => prochainsCreneaux(4), []);
  const boutique = BOUTIQUES.find(b => b.id === boutiqueId) ?? BOUTIQUES[0];

  const [form, setForm] = useState<Form>({
    prenom: '', nom: '', email: '', telephone: '', note: '',
    rue: '', codePostal: '', ville: '',
    creneau: creneaux[0]?.date ?? '',
    paiement: 'ARRIES', carte: '', expiration: '', cvc: '', cgv: false,
  });
  const maj = <K extends keyof Form>(cle: K, valeur: Form[K]) => {
    setForm(f => ({ ...f, [cle]: valeur }));
    setErreurs(e => ({ ...e, [cle]: '' }));
  };

  const frais = fraisLivraison(sousTotal, mode);
  const total = Math.round((sousTotal + frais) * 100) / 100;
  const ar = arrhes(total, mode);
  const paiementComptoirPossible = ar.choixComptoir;

  if (nbArticles === 0 && !confirmee) {
    return (
      <main id="contenu" className="mx-auto max-w-2xl px-5 py-16 text-center">
        <h1 className="text-3xl">Votre panier est vide</h1>
        <p className="mt-3 text-muted">Il n’y a rien à commander pour l’instant.</p>
        <Link to="/boutique" className={clsBouton('primary', 'mt-6')}>Retour au comptoir</Link>
      </main>
    );
  }

  /* ---------------------------------------------------------- confirmation
   * Ce que le client doit pouvoir relire sans appeler : combien il a payé,
   * combien il reste, où et quand il retire, et ce qui va se passer ensuite.
   * Les montants viennent du récapitulatif figé avant le vidage du panier.
   */
  if (confirmee) {
    const r = recap;
    const code = `DV-${confirmee.slice(-4)}`;
    const [hDebut] = (r?.heures.match(/\d{1,2}h\d{2}/) ?? ['10h00'])[0].split('h');
    const hFin = (r?.heures.match(/\d{1,2}h\d{2}/g) ?? ['10h30'])[1]?.split('h')[0] ?? '10';
    const hh = (v: string) => v.padStart(2, '0');
    const debutIso = r ? `${r.dateIso.replace(/-/g, '')}T${hh(hDebut)}0000` : '';
    const finIso = r ? `${r.dateIso.replace(/-/g, '')}T${hh(hFin)}3000` : '';
    const ics = r ? [
      'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Maison Duval//Vitrine//FR', 'BEGIN:VEVENT',
      `UID:${r.numero}@maison-duval.fr`,
      `SUMMARY:Retrait ${r.numero} — Maison Duval`,
      `LOCATION:${r.boutiqueAdresse}`,
      `DESCRIPTION:Commande ${r.numero}. Code de retrait ${code}.`,
      `DTSTART;TZID=Europe/Paris:${debutIso}`, `DTEND;TZID=Europe/Paris:${finIso}`,
      'END:VEVENT', 'END:VCALENDAR',
    ].join('\r\n') : '';
    const lienIcs = `data:text/calendar;charset=utf-8,${encodeURIComponent(ics)}`;

    return (
      <main id="contenu" className="mx-auto max-w-3xl px-5 py-12 sm:py-16">
        <div className="rounded-[24px] bg-surface p-6 shadow-card sm:p-9">
          <div className="flex items-start gap-4">
            <div className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-success-wash text-success">
              <Icon name="check" size={24} />
            </div>
            <div className="min-w-0">
              <p className="eyebrow">Commande enregistrée</p>
              <h1 className="mt-1 font-display text-3xl leading-tight sm:text-4xl">
                Merci{r?.prenom ? `, ${r.prenom}` : ''}.
              </h1>
              <p className="mt-2 text-[0.9375rem] leading-relaxed text-ink-soft">
                Votre commande <strong className="font-semibold">{confirmee}</strong> est transmise à l’atelier.
                {' '}Un SMS part dès que la pesée est faite, avec le montant exact du solde.
              </p>
            </div>
          </div>

          {r && (
            <>
              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                <div className="rounded-2xl bg-linen p-4">
                  <p className="text-[0.6875rem] uppercase tracking-[0.14em] text-muted">Où et quand</p>
                  <p className="mt-1.5 font-semibold capitalize">{r.mode === 'PICKUP' ? r.jour : 'Livraison réfrigérée'}</p>
                  <p className="text-sm text-ink-soft">{r.heures}</p>
                  <p className="mt-1 text-sm text-muted">
                    {r.mode === 'PICKUP' ? `${r.boutiqueNom} — ${r.boutiqueAdresse}` : `Colis isotherme 0–4 °C, ${REGLAGES.livraison.delaiMinH}–${REGLAGES.livraison.delaiMaxH} h ouvrées.`}
                  </p>
                </div>
                <div className="rounded-2xl bg-brass-wash/70 p-4">
                  <p className="text-[0.6875rem] uppercase tracking-[0.14em] text-brass-ink/80">Le compte, à l’euro</p>
                  <dl className="mt-1.5 space-y-1 text-sm">
                    <div className="flex justify-between gap-2"><dt className="text-brass-ink">Payé aujourd’hui</dt><dd className="font-semibold tabular-nums text-brass-ink">{eur(r.arrhes)}</dd></div>
                    <div className="flex justify-between gap-2"><dt className="text-brass-ink">Reste à régler</dt><dd className="tabular-nums text-brass-ink">≈ {eur(r.solde)}</dd></div>
                  </dl>
                  <p className="mt-2 text-[0.75rem] leading-relaxed text-brass-ink/80">
                    Le solde suit le poids réel, dans la tolérance de ±{REGLAGES.poids.tolerancePct} %.
                  </p>
                </div>
              </div>

              <div className="mt-4 rounded-2xl border border-linen-deep/70 p-4">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <p className="text-[0.6875rem] uppercase tracking-[0.14em] text-muted">Code de retrait</p>
                    <p className="font-display text-2xl tracking-[0.08em]">{code}</p>
                    <p className="mt-1 max-w-xs text-[0.75rem] leading-relaxed text-muted">
                      Présentez ce code au comptoir. Votre QR code arrive par SMS avec le même code — pas besoin
                      d’imprimer quoi que ce soit.
                    </p>
                  </div>
                  <div className="flex flex-col gap-2">
                    <a href={lienIcs} download={`${confirmee}.ics`} className={clsBouton('outline', 'btn--sm')}>
                      <Icon name="calendar" size={16} /> Ajouter à mon agenda (.ics)
                    </a>
                    <Link to="/mon-compte" className={clsBouton('quiet', 'btn--sm')}>Suivre ma commande</Link>
                  </div>
                </div>
              </div>

              <div className="mt-4 rounded-2xl bg-linen/70 p-4">
                <p className="text-sm font-semibold">La suite, sans surprise</p>
                <ol className="mt-3 space-y-3">
                  {[
                    'Le boucher prépare votre pièce le matin même.',
                    'Nous pesons : le poids réel et le solde vous sont annoncés par SMS.',
                    r.mode === 'PICKUP' ? 'Vous retirez au comptoir, vous réglez le solde.' : 'Le transporteur vous remet le colis entre 0 et 4 °C.',
                  ].map((t, i) => (
                    <li key={t} className="flex gap-3 text-sm text-ink-soft">
                      <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-surface text-xs font-semibold">{i + 1}</span>
                      {t}
                    </li>
                  ))}
                </ol>
              </div>

              <div className="mt-4 rounded-2xl bg-surface p-4 ring-1 ring-linen-deep/60">
                <p className="text-sm font-semibold">Ce que vous avez commandé</p>
                <ul className="mt-2 space-y-1 text-sm text-ink-soft">
                  {r.lignes.map(l => (
                    <li key={l.nom} className="flex justify-between gap-3">
                      <span>{l.nom} <span className="text-muted">× {l.quantite}</span></span>
                      <span className="tabular-nums">{eur(l.total)}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-2 flex justify-between border-t border-linen-deep/70 pt-2 text-sm font-semibold">
                  <span>Total estimé</span><span className="tabular-nums">{eur(r.total)}</span>
                </div>
              </div>
            </>
          )}

          <p className="mt-6 text-center text-[0.8125rem] text-muted">
            Besoin d’aide ? <a href={`tel:${REGLAGES.marque.telephone.replace(/\s/g, '')}`} className="font-semibold text-ink underline underline-offset-4">{REGLAGES.marque.telephone}</a> — du mardi au samedi, un boucher répond.
          </p>

          <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:justify-center">
            <Link to="/mon-compte" className={clsBouton('primary')}>Suivre ma commande</Link>
            <Link to="/boutique" className={clsBouton('outline')}>Continuer les achats</Link>
          </div>
        </div>
      </main>
    );
  }

  /* ------------------------------------------------------------- validation */
  const valider = (numero: number): boolean => {
    const e: Record<string, string> = {};
    if (numero === 1) {
      if (!form.creneau && mode === 'PICKUP') e.creneau = 'Choisissez un créneau de retrait.';
      if (mode === 'DELIVERY') {
        if (form.rue.trim().length < 4) e.rue = 'Indiquez le numéro et la rue.';
        if (!/^\d{5}$/.test(form.codePostal)) e.codePostal = 'Code postal à cinq chiffres.';
        if (form.ville.trim().length < 2) e.ville = 'Indiquez la ville.';
      }
    }
    if (numero === 2) {
      if (form.prenom.trim().length < 2) e.prenom = 'Votre prénom, pour l’étiquette du colis.';
      if (form.nom.trim().length < 2) e.nom = 'Votre nom.';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email)) e.email = 'Une adresse e-mail valide, pour la confirmation.';
      if (!/^(\+33|0)[1-9](\s?\d{2}){4}$/.test(form.telephone.replace(/\./g, ' '))) e.telephone = 'Un numéro français, pour le SMS de pesée.';
    }
    if (numero === 3) {
      if (form.paiement !== 'COMPTOIR') {
        if (form.carte.replace(/\s/g, '').length < 16) e.carte = 'Seize chiffres, sans espace.';
        if (!/^\d{2}\/\d{2}$/.test(form.expiration)) e.expiration = 'Format MM/AA.';
        if (!/^\d{3,4}$/.test(form.cvc)) e.cvc = 'Trois chiffres au dos.';
      }
      if (!form.cgv) e.cgv = 'Il faut accepter les conditions de vente pour commander.';
    }
    setErreurs(e);
    return Object.keys(e).length === 0;
  };

  const suivant = () => { if (valider(etape)) setEtape(e => Math.min(3, e + 1)); };
  const finaliser = () => {
    if (!valider(3)) return;
    const numero = `CMD-2026-${1060 + Math.floor(Math.random() * 30)}`;
    const creneau = creneaux.find(c => c.date === form.creneau);
    setRecap({
      numero,
      prenom: form.prenom,
      mode,
      jour: creneau?.jour ?? 'créneau choisi',
      heures: creneau?.heures ?? '',
      dateIso: form.creneau,
      boutiqueNom: boutique.nom,
      boutiqueAdresse: boutique.adresse,
      total,
      arrhes: ar.montant,
      solde: Math.max(0, Math.round((total - ar.montant) * 100) / 100),
      lignes: lignes.map(l => ({ nom: l.produit.nom, quantite: l.quantite, total: l.total })),
    });
    setConfirmee(numero);
    vider();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <main id="contenu" className="mx-auto max-w-[1180px] px-5 py-8 sm:px-6">
      <Fil fils={[{ libelle: 'Panier', vers: '/panier' }, { libelle: 'Commande' }]} />
      <h1 className="text-[clamp(1.9rem,4vw,2.8rem)]">Finaliser la commande</h1>

      <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_380px] lg:gap-12">
        <div>
          {/* progression */}
          <ol className="mb-8 flex items-center gap-2 sm:gap-4" aria-label="Étapes de la commande">
            {ETAPES.map((titre, i) => {
              const n = i + 1;
              const active = etape === n, faite = etape > n;
              return (
                <li key={titre} className="flex flex-1 items-center gap-2">
                  <span className={cn('grid h-8 w-8 shrink-0 place-items-center rounded-full text-sm font-semibold transition',
                    active ? 'bg-ink text-lin-50' : faite ? 'bg-success text-white' : 'bg-linen text-muted')}>
                    {faite ? <Icon name="check" size={15} /> : n}
                  </span>
                  <span className={cn('hidden text-sm sm:block', active ? 'font-semibold text-ink' : 'text-muted')}>{titre}</span>
                  {n < ETAPES.length && <span className="h-px flex-1 bg-linen-deep" aria-hidden="true" />}
                </li>
              );
            })}
          </ol>

          {/* ------------------------------------------------------- étape 1 */}
          {etape === 1 && (
            <section aria-labelledby="etape-service">
              <h2 id="etape-service" className="font-display text-2xl">Comment souhaitez-vous être servi ?</h2>
              <div className="mt-4">
                <Segmente bloc libelle="Mode de service" valeur={mode}
                  onChange={v => setMode(v as 'PICKUP' | 'DELIVERY')}
                  options={[{ valeur: 'PICKUP', libelle: 'Retrait en boutique', icone: 'store' },
                            { valeur: 'DELIVERY', libelle: 'Livraison réfrigérée', icone: 'truck' }]} />
              </div>

              {mode === 'PICKUP' ? (
                <div className="mt-5 space-y-4">
                  <Liste label="Boutique de retrait" value={boutiqueId} onChange={e => setBoutique(e.target.value)}
                    options={BOUTIQUES.map(b => ({ valeur: b.id, libelle: `${b.nom} — ${b.adresse}` }))} />
                  <fieldset>
                    <legend className="mb-2 text-sm font-semibold">Créneau de retrait</legend>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {creneaux.map(c => (
                        <label key={c.date}
                          className={cn('flex cursor-pointer items-center justify-between rounded-xl border px-4 py-3 text-sm transition',
                            form.creneau === c.date ? 'border-wine bg-wine-wash' : 'border-linen-deep bg-surface hover:border-laiton-300')}>
                          <span>
                            <span className="block font-medium capitalize">{c.jour}</span>
                            <span className="block text-xs text-muted">{c.heures}</span>
                          </span>
                          <input type="radio" name="creneau" value={c.date} checked={form.creneau === c.date}
                            onChange={() => maj('creneau', c.date)} className="h-4 w-4 accent-wine" />
                        </label>
                      ))}
                    </div>
                    {erreurs.creneau && <p className="field__error mt-2" role="alert"><Icon name="info" size={15} />{erreurs.creneau}</p>}
                  </fieldset>
                </div>
              ) : (
                <div className="mt-5 grid gap-4 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <Saisie label="Adresse" placeholder="12 rue des Pêcheurs" value={form.rue} obligatoire
                      autoComplete="address-line1" erreur={erreurs.rue} onChange={e => maj('rue', e.target.value)} />
                  </div>
                  <Saisie label="Code postal" placeholder="95120" value={form.codePostal} obligatoire inputMode="numeric"
                    autoComplete="postal-code" erreur={erreurs.codePostal} onChange={e => maj('codePostal', e.target.value)} />
                  <Saisie label="Ville" placeholder="Sannois" value={form.ville} obligatoire
                    autoComplete="address-level2" erreur={erreurs.ville} onChange={e => maj('ville', e.target.value)} />
                  <p className="sm:col-span-2 rounded-xl bg-linen px-4 py-3 text-[0.8125rem] text-ink-soft">
                    <Icon name="snow" size={15} className="mr-1 inline text-info" />
                    Livraison en {REGLAGES.livraison.delaiMinH}–{REGLAGES.livraison.delaiMaxH} h ouvrées, commande avant {REGLAGES.livraison.coupure}.
                    Zones desservies : {REGLAGES.livraison.zonesCoursier.slice(0, 4).join(', ')}…
                  </p>
                </div>
              )}

              <Bouton bloc taille="lg" className="mt-6" onClick={suivant}>Continuer</Bouton>
            </section>
          )}

          {/* ------------------------------------------------------- étape 2 */}
          {etape === 2 && (
            <section aria-labelledby="etape-coordonnees">
              <h2 id="etape-coordonnees" className="font-display text-2xl">Vos coordonnées</h2>
              <p className="mt-1 text-sm text-muted">Elles servent à l’étiquette, au SMS de pesée et à la confirmation. Rien d’autre.</p>
              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                <Saisie label="Prénom" value={form.prenom} obligatoire autoComplete="given-name"
                  erreur={erreurs.prenom} onChange={e => maj('prenom', e.target.value)} />
                <Saisie label="Nom" value={form.nom} obligatoire autoComplete="family-name"
                  erreur={erreurs.nom} onChange={e => maj('nom', e.target.value)} />
                <Saisie label="E-mail" type="email" value={form.email} obligatoire autoComplete="email"
                  aide="La confirmation et le lien de suivi y sont envoyés." erreur={erreurs.email}
                  onChange={e => maj('email', e.target.value)} />
                <Saisie label="Téléphone" type="tel" value={form.telephone} obligatoire autoComplete="tel"
                  aide="Un SMS part à la pesée : c’est là qu’on annonce le solde." erreur={erreurs.telephone}
                  onChange={e => maj('telephone', e.target.value)} />
                <div className="sm:col-span-2">
                  <ZoneTexte label="Un mot pour le boucher (facultatif)" value={form.note}
                    placeholder="Épaisseur, parage, ficelage, heure de passage…"
                    onChange={e => maj('note', e.target.value)} />
                </div>
              </div>
              <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row">
                <Bouton variante="ghost" onClick={() => setEtape(1)}>Retour</Bouton>
                <Bouton bloc taille="lg" onClick={suivant}>Continuer</Bouton>
              </div>
            </section>
          )}

          {/* ------------------------------------------------------- étape 3 */}
          {etape === 3 && (
            <section aria-labelledby="etape-paiement">
              <h2 id="etape-paiement" className="font-display text-2xl">Paiement</h2>

              <div className="mt-4">
                <BlocPaiement total={total} ar={ar} />
              </div>

              <fieldset className="mt-5">
                <legend className="mb-2 text-sm font-semibold">Comment souhaitez-vous régler ?</legend>
                <div className="space-y-2">
                  {ar.montant > 0 && (
                    <label className={cn('flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition',
                      form.paiement === 'ARRIES' ? 'border-wine bg-wine-wash' : 'border-linen-deep bg-surface')}>
                      <input type="radio" name="paiement" checked={form.paiement === 'ARRIES'}
                        onChange={() => maj('paiement', 'ARRIES')} className="mt-1 h-4 w-4 accent-wine" />
                      <span className="flex-1">
                        <span className="block text-sm font-semibold">Payer {eur(ar.montant)} d’arrhes maintenant</span>
                        <span className="block text-xs text-muted">Le solde se règle au retrait, après pesée.</span>
                      </span>
                    </label>
                  )}
                  {paiementComptoirPossible && (
                    <label className={cn('flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition',
                      form.paiement === 'COMPTOIR' ? 'border-wine bg-wine-wash' : 'border-linen-deep bg-surface')}>
                      <input type="radio" name="paiement" checked={form.paiement === 'COMPTOIR'}
                        onChange={() => maj('paiement', 'COMPTOIR')} className="mt-1 h-4 w-4 accent-wine" />
                      <span className="flex-1">
                        <span className="block text-sm font-semibold">Tout régler au comptoir</span>
                        <span className="block text-xs text-muted">Possible pour ce panier — la commande reste réservée.</span>
                      </span>
                    </label>
                  )}
                  {ar.montant === 0 && !paiementComptoirPossible && mode === 'DELIVERY' && (
                    <label className="flex items-start gap-3 rounded-xl border border-wine bg-wine-wash p-4">
                      <input type="radio" checked readOnly className="mt-1 h-4 w-4 accent-wine" />
                      <span>
                        <span className="block text-sm font-semibold">Payer {eur(total)} en ligne</span>
                        <span className="block text-xs text-muted">La livraison se règle intégralement à la commande.</span>
                      </span>
                    </label>
                  )}
                </div>
              </fieldset>

              {form.paiement !== 'COMPTOIR' && (
                <div className="mt-5 grid gap-4 sm:grid-cols-[1fr_120px_100px]">
                  <Saisie label="Numéro de carte" value={form.carte} inputMode="numeric" placeholder="4242 4242 4242 4242"
                    autoComplete="cc-number" erreur={erreurs.carte} onChange={e => maj('carte', e.target.value)} />
                  <Saisie label="Expiration" value={form.expiration} placeholder="MM/AA" autoComplete="cc-exp"
                    erreur={erreurs.expiration} onChange={e => maj('expiration', e.target.value)} />
                  <Saisie label="CVC" value={form.cvc} inputMode="numeric" placeholder="123" autoComplete="cc-csc"
                    erreur={erreurs.cvc} onChange={e => maj('cvc', e.target.value)} />
                </div>
              )}

              <div className="mt-5">
                <CaseACocher label="J’accepte les conditions de vente"
                  sousTitre="Les arrhes réservent la pièce et ne sont pas remboursables en cas de non-retrait, sauf accord de la maison."
                  checked={form.cgv} onChange={e => maj('cgv', e.target.checked)} />
                {erreurs.cgv && <p className="field__error mt-2" role="alert"><Icon name="info" size={15} />{erreurs.cgv}</p>}
              </div>

              <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row">
                <Bouton variante="ghost" onClick={() => setEtape(2)}>Retour</Bouton>
                <Bouton bloc taille="lg" onClick={finaliser}>
                  {form.paiement === 'COMPTOIR' ? 'Confirmer la commande' : `Payer ${eur(form.paiement === 'ARRIES' ? ar.montant : total)}`}
                </Bouton>
              </div>
              <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-muted">
                <Icon name="shield" size={14} /> Démonstration : aucune carte n’est débitée, aucune donnée n’est transmise.
              </p>
            </section>
          )}
        </div>

        {/* --------------------------------------------------------- récapitulatif */}
        <aside className="lg:sticky lg:top-[120px] lg:self-start">
          <div className="rounded-[22px] bg-surface p-5 shadow-card">
            <h2 className="font-display text-xl">Votre commande</h2>
            <ul className="mt-3 space-y-3">
              {lignes.map(l => (
                <li key={l.cle} className="flex justify-between gap-3 text-sm">
                  <span className="min-w-0">
                    <span className="block truncate font-medium">{l.produit.nom}</span>
                    <span className="block text-xs text-muted">
                      {l.produit.modele === 'AU_KG' ? kg(l.poidsG) : 'à la pièce'} × {l.quantite}
                    </span>
                  </span>
                  <span className="shrink-0 tabular-nums">{eur(l.total)}</span>
                </li>
              ))}
            </ul>
            <dl className="mt-4 space-y-1.5 border-t border-linen-deep/70 pt-3 text-sm">
              <div className="flex justify-between"><dt className="text-ink-soft">Sous-total</dt><dd>{eur(sousTotal)}</dd></div>
              <div className="flex justify-between"><dt className="text-ink-soft">{mode === 'PICKUP' ? 'Retrait' : 'Livraison'}</dt><dd>{frais === 0 ? 'Offert' : eur(frais)}</dd></div>
              <div className="flex justify-between border-t border-linen-deep/70 pt-2 text-base font-semibold"><dt>Total</dt><dd>{eur(total)}</dd></div>
            </dl>
            {mode === 'PICKUP' && (
              <p className="mt-3 flex items-start gap-2 rounded-xl bg-linen px-3 py-2.5 text-[0.8125rem] text-ink-soft">
                <Icon name="store" size={16} className="mt-0.5 shrink-0" />
                {boutique.nom} — {creneaux.find(c => c.date === form.creneau)?.jour ?? 'créneau à choisir'}
              </p>
            )}
          </div>
        </aside>
      </div>
    </main>
  );
}
